import { guardTab } from "@/hooks/useDeskNavigate";
import { hasRight } from "@/components/crm/crmShared";
import HistoryPanel from "@/components/common/HistoryPanel";
import { useState, useEffect, useMemo } from "react";
import HistoryTimeline, { HISTORY_COLORS } from "@/components/ui/HistoryTimeline";
import { toast } from "@/lib/toast";
import { History, Search, CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { OutlookTabs } from "@/components/common/OutlookTabs";
import SearchClearButton from "@/components/ui/SearchClearButton";
import { useUserDirectory } from "@/lib/userDirectory";
import OtpIncidentsPanel from "@/components/settings/OtpIncidentsPanel";
import ImpersonationsPanel from "@/components/settings/ImpersonationsPanel";
import { useAuth } from "@/context/AuthContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import api from "@/lib/api";

const REASON_LABEL = {
  wrong_code: "Code incorrect",
  expired: "Code expiré",
  no_active_code: "Aucun code actif",
  auto_blocked: "Blocage automatique",
  blocked: "Compte bloqué",
  activation: "Activation du compte",
};

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function shortUA(ua) {
  if (!ua) return "—";
  // Non-browser clients first (they don't carry a browser signature)
  const client =
    /python-requests\/([\d.]+)/i.exec(ua)?.[0] ||
    /curl\/([\d.]+)/i.exec(ua)?.[0] ||
    /Wget\/([\d.]+)/i.exec(ua)?.[0] ||
    /HTTPie\/([\d.]+)/i.exec(ua)?.[0] ||
    /PostmanRuntime\/([\d.]+)/i.exec(ua)?.[0] ||
    /Insomnia\/([\d.]+)/i.exec(ua)?.[0] ||
    /axios\/([\d.]+)/i.exec(ua)?.[0] ||
    /Go-http-client\/([\d.]+)/i.exec(ua)?.[0];
  if (client) return client + " · API";

  // Browsers — Edge & Opera checked before Chrome because they include the "Chrome/" token too
  const browser =
    (/Edg\/([\d.]+)/.exec(ua)?.[0]?.replace("Edg/", "Edge "))
    || (/OPR\/([\d.]+)/.exec(ua)?.[0]?.replace("OPR/", "Opera "))
    || (/Firefox\/([\d.]+)/.exec(ua)?.[0])
    || (/HeadlessChrome\/([\d.]+)/.exec(ua)?.[0]?.replace("HeadlessChrome/", "Chrome (headless) "))
    || (/Chrome\/([\d.]+)/.exec(ua)?.[0])
    || (/Version\/([\d.]+).*Safari/.exec(ua) && "Safari " + /Version\/([\d.]+)/.exec(ua)[1])
    || (/Safari\/([\d.]+)/.exec(ua) && "Safari (Mobile)")
    || "Client inconnu";

  const os =
    /Windows NT [\d.]+/.exec(ua)?.[0]?.replace("Windows NT", "Windows")
    || /Mac OS X [\d._]+/.exec(ua)?.[0]?.replace(/_/g, ".")
    || /Android [\d.]+/.exec(ua)?.[0]
    || /iPhone OS [\d._]+/.exec(ua)?.[0]?.replace(/_/g, ".")?.replace("iPhone OS", "iOS")
    || /Linux/.exec(ua)?.[0]
    || "";
  return `${browser}${os ? " · " + os : ""}`;
}

export default function LoginHistorySection() {
  const { user } = useAuth();
  const isFirstAdmin = hasRight(user, "settings.login_history"); // iter203 — droit de la matrice (plus de premier-admin en dur)
  const [subTab, setSubTab] = useState("connections");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/login-history", { params: { limit: 200 } });
      setEvents(data || []);
    } catch {
      toast.error("Impossible de charger l'historique");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return events.filter((e) => {
      if (filter === "success" && !e.success) return false;
      if (filter === "failed" && e.success) return false;
      if (!q) return true;
      return (
        e.email?.toLowerCase().includes(q) ||
        e.ip?.toLowerCase().includes(q) ||
        (e.reason || "").toLowerCase().includes(q)
      );
    });
  }, [events, search, filter]);

  return (
    <Card className="bg-card border-vertec-borderDark p-8">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-semibold mb-1 flex items-center gap-2">
            <History size={22} className="text-vertec-blue" /> Historique de connexion
          </h2>
          <p className="text-muted-foreground text-sm">
            Toutes les tentatives — réussies ou échouées — sont journalisées ici pour auditer
            l&apos;accès à l&apos;espace. L&apos;onglet <strong>Incidents</strong> regroupe les échecs d&apos;envoi OTP détectés automatiquement.
          </p>
        </div>
      </div>

      <Tabs value={subTab} onValueChange={guardTab(setSubTab)} className="w-full">
        <OutlookTabs module="settings" value={subTab} onChange={setSubTab} testId="login-history-tabs" style={{ padding: 4, background: "#101013", border: "1px solid #26262b", borderRadius: 12, width: "fit-content" }}
          tabs={[{ key: "connections", label: "Connexions", icon: "history", testId: "subtab-connections" }, { key: "incidents", label: "Incidents", icon: "alert", testId: "subtab-incidents" }, ...(isFirstAdmin ? [{ key: "impersonations", label: "Impersonations", icon: "eye", testId: "subtab-impersonations" }] : [])]} />

        <TabsContent value="connections" className="mt-5 space-y-5">
          <div className="flex items-center justify-end">
            <Button
              variant="outline"
              className="border-vertec-borderDark gap-2 shrink-0"
              onClick={load}
              disabled={loading}
              data-testid="btn-refresh-history"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Actualiser
            </Button>
          </div>
          <ConnectionsTable
            events={events}
            filtered={filtered}
            loading={loading}
            search={search}
            setSearch={setSearch}
            filter={filter}
            setFilter={setFilter}
          />
        </TabsContent>

        <TabsContent value="incidents" className="mt-5">
          <OtpIncidentsPanel />
        </TabsContent>

        {isFirstAdmin && (
          <TabsContent value="impersonations" className="mt-5">
            <ImpersonationsPanel />
          </TabsContent>
        )}
      </Tabs>
    </Card>
  );
}

function ConnectionsTable({ events, filtered, loading, search, setSearch, filter, setFilter }) {
  const { byId: dirById, users: dirUsers } = useUserDirectory();
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
        <div className="relative md:col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <Input
            placeholder="Rechercher (e-mail, IP, raison)…"
            className="pl-9 pr-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="history-search"
            data-no-capitalize
          />
          <SearchClearButton value={search} onClear={() => setSearch("")} testid="history-search-clear" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="history-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les tentatives</SelectItem>
            <SelectItem value="success">Réussies</SelectItem>
            <SelectItem value="failed">Échouées</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm py-10 text-center">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="text-muted-foreground text-sm py-10 text-center border border-dashed border-vertec-borderDark rounded-md">
          Aucun événement à afficher.
        </div>
      ) : (
        <div className="rounded-md border border-vertec-borderDark overflow-hidden" data-testid="login-history-list">
          <HistoryPanel entityType="login" entityId="all" label="Connexions" entityLabel="les connexions" height={560} testId="login-history-timeline" />
        </div>
      )}
    </>
  );
}
